import inputVariables from "@krystal/css/components/input?inline";
import { html, nothing, unsafeCSS, type HTMLTemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { MaskInput } from "maska";
import { cn } from "@/utilities/css";
import EkoElement from "@/utilities/element";
import { createSlotChangeHandler, setInitialSlotProperties } from "@/utilities/slot";
import "@/components/icon";
import "@/components/tooltip";
import "@/components/label";
import "@/components/field/hint";
import style from "./input.css?inline";

type SlottedElement = "eko-avatar" | "eko-flag" | "eko-icon" | "eko-dot" | null;

@customElement("eko-input")
export class Input extends EkoElement {
  static formAssociated = true;

  static slotPropertyMap = {
    after: "afterElement",
    before: "beforeElement",
    prefix: "prefixElement",
    suffix: "suffixElement",
  } as const;

  static styles = [...EkoElement.styles, unsafeCSS(inputVariables), unsafeCSS(style)];

  protected slottedInput?: HTMLInputElement;

  private _defaultValue: string | undefined = undefined;

  private handleBlur = () => {
    this.dispatchEvent(new FocusEvent("blur", { bubbles: true }));
    this.checkValidity();
  };

  private handleFocus = () => {
    this.dispatchEvent(new FocusEvent("focus", { bubbles: true }));
  };

  private handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.updateFormValue();
    this.checkValidity();

    this.dispatchEvent(
      new CustomEvent("input", {
        bubbles: true,
        detail: { value: this.value },
      })
    );
  };

  private handlePasswordToggle = () => {
    this.passwordVisible = !this.passwordVisible;
    if (this.inputElement) {
      this.inputElement.type = this.passwordVisible ? "text" : "password";
    }
  };

  private handleSlotChange = createSlotChangeHandler(this, Input.slotPropertyMap);

  private inputRef: Ref<HTMLInputElement> = createRef();

  private internals: ElementInternals;

  private maskaInstance: MaskInput | null = null;

  private onePasswordObserver: MutationObserver | null = null;

  @state() private passwordVisible: boolean = false;
  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @state() afterElement: SlottedElement = null;
  @property() autocomplete: string = "";
  @state() beforeElement: SlottedElement = null;
  @property() class: string = "";
  @property() description: string = "";
  @property({ reflect: true, type: Boolean }) disabled: boolean = false;
  @property() error: string = "";
  @property() help: string = "";
  @property() id: string = `input-${Math.random().toString(36).substr(2, 9)}`;
  @property() inputMode: string = "";
  @property() label: string = "";
  @property() mask: string = "";
  @property() name: string = "";
  @property() placeholder: string = "";
  @state() prefixElement: SlottedElement = null;
  @property({ reflect: true, type: Boolean }) readonly: boolean = false;
  @property({ type: Boolean }) required: boolean = false;
  @property({ type: Boolean }) slotted: boolean = false;
  @state() suffixElement: SlottedElement = null;
  @property() type: string = "text";

  @property() value: string = "";

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private destroyMaska() {
    if (this.maskaInstance) {
      this.maskaInstance.destroy();
      this.maskaInstance = null;
    }
  }

  private handleInputSlotChange() {
    if (!this.slotted) return;

    const input = this.querySelector('input[slot="input"]') as HTMLInputElement | null;
    if (!input) return;

    this.slottedInput = input;
    input.oninput = (e: Event) => this.handleInput(e);
    input.onfocus = () => this.handleFocus();
    input.onblur = () => this.handleBlur();

    if (input.type) {
      this.type = input.type;
    }

    const changedProperties = new Map<string, any>();
    changedProperties.set("value", input.value);
    changedProperties.set("disabled", input.disabled);
    changedProperties.set("required", input.required);
    this.updated(changedProperties);
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (this.disabled || this.slottedInput?.disabled || this.inputElement?.disabled) {
      return;
    }

    if (e.key === "Enter") {
      this.internals.form?.requestSubmit();
    }
  }

  private setupMaska() {
    if ((this.mask || this.inputMode === "numeric") && this.inputElement) {
      this.destroyMaska();

      const conditionalOptions: any = {};

      if (this.inputMode === "numeric") {
        conditionalOptions.number = {};
      }

      this.maskaInstance = new MaskInput(this.inputElement, {
        mask: this.mask,
        ...conditionalOptions,
        onMaska: (detail) => {
          this.value = detail.masked;
          this.updateFormValue();

          this.dispatchEvent(
            new CustomEvent("maska", {
              bubbles: true,
              detail,
            })
          );
        },
      });
    }
  }

  private updateFormValue() {
    if (this.value) {
      this.internals.setFormValue(this.value);
    } else {
      this.internals.setFormValue(null);
    }
  }

  checkValidity(): boolean {
    const input = this.inputElement;
    if (!input) return true;

    // Use native validation first
    const isNativeValid = input.checkValidity();

    if (!isNativeValid) {
      this.internals.setValidity(input.validity, input.validationMessage, input);
      return false;
    }

    // Clear validation state if valid
    this.internals.setValidity({});
    return true;
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateComplete.then(() => {
      if (!this.isConnected) return;
      this.setupMaska();
      if (globalThis.MutationObserver && this.inputElement) {
        this.inputElement.removeAttribute("data-com-onepassword-filled");
        this.onePasswordObserver = new globalThis.MutationObserver(() => {
          this.inputElement?.removeAttribute("data-com-onepassword-filled");
        });
        this.onePasswordObserver.observe(this.inputElement, {
          attributeFilter: ["data-com-onepassword-filled"],
        });
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroyMaska();
    this.onePasswordObserver?.disconnect();
    this.onePasswordObserver = null;
  }

  firstUpdated() {
    setInitialSlotProperties(this, Input.slotPropertyMap);
    this.handleInputSlotChange();
    // Capture the default value after initial render (when attributes are parsed)
    if (this._defaultValue === undefined) {
      this._defaultValue = this.value || "";
    }
  }

  focus() {
    if (this.inputElement) {
      this.inputElement.focus();
    }
  }

  // Form lifecycle methods
  formAssociatedCallback() {
    // Called when associated with a form
    // Store the original/default value for reset if not already set
    if (this._defaultValue === undefined) {
      this._defaultValue = this.value || "";
    }
    this.internals.setFormValue(this.value);
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    // Reset to the original/default value (use empty string if not set)
    const resetValue = this._defaultValue !== undefined ? this._defaultValue : "";
    this.value = resetValue;
    if (this.inputElement) {
      this.inputElement.value = resetValue;
    }
    this.internals.setFormValue(resetValue || null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    this.value = state || "";
    if (this.inputElement) {
      this.inputElement.value = this.value;
    }
  }

  render() {
    const descriptionId = this.description ? `${this.id}-description` : "";
    const helpId = this.help && this.type !== "password" ? `${this.id}-help` : "";

    return html`
      <div
        class=${cn("eko-input", this.class)}
        prefix=${ifDefined(this.prefixElement)}
        suffix=${ifDefined(this.suffixElement)}
        before=${ifDefined(this.beforeElement)}
        after=${ifDefined(this.afterElement)}
        part="root">
        ${this.label
          ? html`
              <eko-label
                for=${this.id}
                ?required=${this.required}
                ?admin=${this.admin}
                part="label">
                ${this.label}
              </eko-label>
            `
          : nothing}

        <div class="eko-input__wrapper" part="wrapper">
          <slot
            name="prefix"
            @slotchange=${this.handleSlotChange}
            class="${when(this.prefixElement, () => "eko-input__prefix")}"></slot>

          <div class="eko-input__field-wrapper" part="field-wrapper">
            <slot
              name="before"
              @slotchange=${this.handleSlotChange}
              class=${when(this.beforeElement, () => "eko-input__before")}></slot>
            <div class="eko-input__field-wrapper-content" part="field-wrapper-content">
              <slot></slot>
              ${this.slotted
                ? html`<slot name="input" @slotchange=${this.handleInputSlotChange}></slot>`
                : this.renderInput({ descriptionId, helpId })}
            </div>
            ${this.type === "password"
              ? html`
                  <eko-tooltip
                    position="right"
                    arrow
                    strategy="fixed"
                    class="eko-input__password-toggle"
                    part="password-toggle"
                    @click=${this.handlePasswordToggle}>
                    <eko-icon
                      name=${this.passwordVisible ? "eye-off" : "eye"}
                      theme="current"
                      size="md"
                      part="password-toggle-icon"></eko-icon>
                    <span slot="content"
                      >${this.passwordVisible ? "Hide password" : "Show password"}</span
                    >
                  </eko-tooltip>
                `
              : this.help || this.hasError
                ? html`
                    <eko-tooltip
                      position="right"
                      arrow
                      strategy="fixed"
                      id=${helpId}
                      aria-label=${this.help || this.error}
                      class="eko-input__help"
                      part="help">
                      <eko-icon
                        name=${this.hasError ? "alert-circle" : "help-circle"}
                        theme="current"
                        size="md"
                        part="help-icon"></eko-icon>
                      <span slot="content">${this.help || this.error}</span>
                    </eko-tooltip>
                  `
                : nothing}
            <slot
              name="after"
              @slotchange=${this.handleSlotChange}
              class=${when(this.afterElement, () => "eko-input__after")}></slot>
          </div>
          <slot
            name="suffix"
            @slotchange=${this.handleSlotChange}
            class="${when(this.suffixElement, () => "eko-input__suffix")}"></slot>
        </div>
        ${this.hasError || this.description
          ? html`<div class="eko-input__hint" part="hint">
              ${this.hasError
                ? html`<eko-hint error part="error">${this.error}</eko-hint>`
                : nothing}
              ${this.description
                ? html`
                    <eko-hint id=${descriptionId} part="description">
                      ${this.description}
                    </eko-hint>
                  `
                : nothing}
            </div> `
          : nothing}
      </div>
    `;
  }

  renderInput({
    descriptionId,
    helpId,
  }: {
    descriptionId: string;
    helpId: string;
  }): HTMLTemplateResult | null {
    return html`<input
      ${ref(this.inputRef)}
      id=${this.id}
      type=${this.type === "password" && this.passwordVisible ? "text" : this.type}
      inputmode=${this.inputMode}
      name=${this.name}
      value=${this.value}
      placeholder=${this.placeholder}
      ?required=${this.required}
      ?disabled=${this.disabled}
      autocomplete=${this.autocomplete}
      ?readonly=${this.readonly}
      ?admin=${this.admin}
      aria-describedby=${[descriptionId, helpId].filter(Boolean).join(" ")}
      class="eko-input__field"
      @keydown=${this.handleKeyDown}
      @input=${this.handleInput}
      @focus=${this.handleFocus}
      @blur=${this.handleBlur} />`;
  }

  reportValidity(): boolean {
    return this.internals.reportValidity();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("value")) {
      // Store the default value if this is the first time value is set and we haven't stored it yet
      if (this._defaultValue === undefined) {
        this._defaultValue = this.value || "";
      }
      this.updateFormValue();
      if (this.inputElement) {
        this.inputElement.value = this.value;
      }
    }

    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }

    if (changedProperties.has("mask")) {
      this.updateComplete.then(() => {
        this.setupMaska();
      });
    }
  }

  get form() {
    return this.internals.form;
  }

  private get hasError(): boolean {
    return this.error !== "";
  }

  protected get inputElement(): HTMLInputElement | undefined {
    if (this.slottedInput) return this.slottedInput;
    const refValue = this.inputRef?.value;
    return refValue && refValue instanceof HTMLInputElement ? refValue : undefined;
  }

  get validationMessage(): string {
    return this.internals.validationMessage;
  }

  get validity(): globalThis.ValidityState {
    return this.internals.validity;
  }

  get willValidate(): boolean {
    return !this.disabled;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-input": Input;
  }
}
